'use client';

import React, { useEffect, useRef, useState } from 'react';
import type { Release as ReleaseType } from '../../types';
import { getBasePath } from '../../utils/basePath';
import MediaButtons from '../MediaButtons';
import {
  StyledCaption,
  StyledCol,
  StyledH1,
  StyledP,
  StyledReleaseBgRow,
  StyledReleaseContainer,
  StyledReleaseImage,
} from '../styled';

interface ReleaseProps extends ReleaseType {
  handleClick?: () => void;
}

/**
 * Full-viewport release showcase with blurred canvas backdrop, responsive animations, and scroll autoplay trigger.
 */
export const Release = React.forwardRef<HTMLDivElement, ReleaseProps>((props, ref) => {
  const {
    cover,
    bgColor,
    name,
    date,
    short_description,
    links,
    tracklist,
    detail_link,
    handleClick,
  } = props;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [isInViewport, setIsInViewport] = useState(false);

  // IntersectionObserver for scroll animations
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInViewport(entry.isIntersecting);
      },
      { threshold: 0.3 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Blurred-canvas background
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const basePath = getBasePath();
    const img = new Image();
    img.src = `${basePath}/assets/${cover}`;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let isMounted = true;
    img.onload = () => {
      if (!isMounted) return;
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;

      const wrh = img.width / img.height;
      let newWidth = canvas.width;
      let newHeight = newWidth / wrh;

      if (newHeight > canvas.height) {
        newHeight = canvas.height;
        newWidth = newHeight * wrh;
      }

      const xOffset = newWidth < canvas.width ? (canvas.width - newWidth) / 2 : 0;
      const yOffset = newHeight < canvas.height ? (canvas.height - newHeight) / 2 : 0;

      ctx.filter = 'blur(6px)';
      ctx.drawImage(img, xOffset, yOffset, newWidth, newHeight);
    };

    return () => {
      isMounted = false;
    };
  }, [cover]);

  const basePath = getBasePath();

  return (
    <StyledReleaseContainer
      ref={(el) => {
        containerRef.current = el;
        if (typeof ref === 'function') ref(el);
        else if (ref) ref.current = el;
      }}
      id={`release-${detail_link}`}
    >
      <StyledReleaseBgRow style={{ backgroundColor: `${bgColor}` }}>
        <canvas
          className={isInViewport ? 'fadeInBg' : 'fadeOut'}
          ref={canvasRef}
          style={{
            transform: 'scale(1.2)',
            margin: 'auto',
          }}
        />
        <img ref={imageRef} src={`${basePath}/assets/${cover}`} className="hidden" alt="" />
      </StyledReleaseBgRow>
      <div
        className="row g-0 m-0 w-100 justify-content-center align-items-center position-relative"
        style={{ zIndex: 10 }}
      >
        <div className="col-12 col-md-12 col-lg-12 text-center">
          <StyledReleaseImage
            className={isInViewport ? 'visibleCover' : undefined}
            style={{
              backgroundImage: `url(${basePath}/assets/${cover})`,
            }}
            id={name}
            onClick={handleClick}
            role="button"
            tabIndex={0}
            aria-label={`View ${name} details`}
          />
        </div>
        <StyledCol
          className={
            isInViewport
              ? 'visible mx-auto col-11 col-md-9 scaleDownHor p-2'
              : 'hidden col-11 col-md-9 mx-auto scaleDownHor p-2'
          }
        >
          <StyledH1>{name}</StyledH1>
          <StyledP>{short_description}</StyledP>
          <StyledCaption>
            released: {date} {tracklist.length > 1 ? `| Tracks: ${tracklist.length}` : ''}
          </StyledCaption>
        </StyledCol>
        <div className="col-12 col-md-3 col-lg-3 d-flex justify-content-center align-items-center mediaButtonsXS">
          <MediaButtons bgColor={bgColor} links={links} isInViewport={isInViewport} />
        </div>
      </div>
    </StyledReleaseContainer>
  );
});

Release.displayName = 'Release';

export default Release;
